import { Box, Grid } from "@mui/material";
import React, { useEffect, useState } from "react";
import { ITaskTypeCol, taskTypeColJsonSerializerReplacer } from "ui/Scripts/source/models/task/taskTypeCol";
import { Translations } from "ui/Scripts/source/models/translations";
import { getExtraParametersFromReportFilter, getExtraParametersTaskType, ITaskTypeFieldPropUpdate } from "../../FormDesignerTypes";
import { FormField } from "ui/Scripts/source/components/framework/FormField";
import { AppReportTaskTypeEventReference } from "ui/Scripts/source/models/appReportTemplate/appReportTaskTypeEventReference";
import { IIdTitleNode } from "ui/Scripts/source/models/common/idTitleNode";
import { IdTitleDropdown } from "ui/Scripts/source/components/framework/Dropdown/IdTitleDropdown";
import { EventTypeNotSetId, getEventGeneralFilterFromFilterState, getFilterStatePropsFromEventGeneralFilter, IEventPageFilterState } from "source/components/event/EventPage/EventPageUtils";
import { ITaskDataRow, TaskDataRow } from "ui/Scripts/source/models/task/taskDataRow";
import { IIdTitle } from "ui/Scripts/source/models/common/idTitle";
import { ITaskEditTypeItem } from "ui/Scripts/source/models/task/taskEditTypeItem";
import { EventStateFilter } from "ui/Scripts/source/models/enums";
import { Base } from "ui/Scripts/source/framework/base";
import { ITaskDataRowCol } from "ui/Scripts/source/models/task/taskDataRowCol";
import { TaskDynamicForm } from "source/components/task/TaskDynamicForm/TaskDynamicForm";
import { TreeCheckboxDropdown } from "ui/Scripts/source/components/framework/Dropdown/TreeCheckboxDropdown";
import { IdColorTitle } from "ui/Scripts/source/models/common/idColorTitle";
import { CheckboxDropdown } from "ui/Scripts/source/components/framework/Dropdown/CheckboxDropdown";
import { SaveReportDialog } from "source/components/report/PageNavMenuItemReportList/SaveReportDialog";

interface IFormDesignerComponentPropertiesEventRefProps {
    companyId: string;
    formFieldMargin: string;
    taskTypeCol?: ITaskTypeCol;
    formDataCol: ITaskDataRowCol;
    eventReports: IIdTitleNode[];
    eventUnits: IIdTitleNode[];
    eventStates: IIdTitle[];
    eventUrgencyClasses: IdColorTitle[];
    eventTypes: IIdTitleNode[];
    eventTypeBs: IIdTitleNode[];
    eventTypeCs: IIdTitleNode[];
    onTaskTypePropUpdate?: (values: ITaskTypeFieldPropUpdate[]) => void;
}

interface IFormDesignerComponentPropertiesEventRefState extends IEventPageFilterState {
    taskType: ITaskEditTypeItem;
    extraParameters: ITaskDataRow;
}

export const FormDesignerComponentPropertiesEventRef = (
    props: IFormDesignerComponentPropertiesEventRefProps
) => {
    const objectValue = new AppReportTaskTypeEventReference(props.taskTypeCol.objectValue);

    const [state, setState] = useState<IFormDesignerComponentPropertiesEventRefState>(() => {
        const taskType = getExtraParametersTaskType();
        const extraParameters = getExtraParametersFromReportFilter(taskType, new TaskDataRow(), props.taskTypeCol.objectValue?.reportFilter ?? {});
        return {
            ...getFilterStatePropsFromEventGeneralFilter(objectValue.generalFilter),
            taskType,
            extraParameters
        };
    });

    useEffect(() => {
        if (!props.taskTypeCol.objectValue?.generalFilter) return;
        setState((old) => ({
            ...old,
            ...getFilterStatePropsFromEventGeneralFilter(props.taskTypeCol.objectValue.generalFilter)
        }));
    }, [props.taskTypeCol.objectValue?.generalFilter]);

    useEffect(() => {
        if (!props.taskTypeCol.objectValue?.reportFilter) return;
        setState((old) => ({
            ...old,
            extraParameters: getExtraParametersFromReportFilter(state.taskType, old.extraParameters, props.taskTypeCol.objectValue.reportFilter)
        }));
    }, [props.taskTypeCol.objectValue?.reportFilter]);

    const handleObjectValueChange = (obj) => {
        props.onTaskTypePropUpdate([{
            field: props.taskTypeCol?.field,
            name: "objectValue",
            value: obj
        }]);
    };

    const getName = (reportId: string, chartId: string): string => {
        const aReport = props.eventReports.find(r => r.id === reportId);
        const aChart = aReport?.children.find(c => c.id === chartId);
        return `${aReport?.title || ""}, ${aChart?.title || ""}`;
    };

    const getGeneralFilter = (newState: IFormDesignerComponentPropertiesEventRefState): any => {
        const generalFilter = getEventGeneralFilterFromFilterState(newState);
        const json = JSON.stringify(generalFilter,
            taskTypeColJsonSerializerReplacer,
            "\t"
        );
        return JSON.parse(json);
    };

    const report = props.eventReports.find(r => r.id === objectValue.reportId);

    const startDateStr = props.formDataCol?.valueObj?.startDate
        ? Base.utcTimeToDateStr(props.formDataCol?.valueObj?.startDate)
        : "";

    const endDateStr = props.formDataCol?.valueObj?.endDate
        ? Base.utcTimeToDateStr(props.formDataCol?.valueObj?.endDate)
        : "";

    return (
        <>
            {!!props.formDataCol?.valueObj?.startDate && !!props.formDataCol?.valueObj?.endDate &&
                <Grid item sm={12}>
                    <FormField title={Translations.Interval} margin={props.formFieldMargin}>
                        {`${startDateStr} - ${endDateStr}`}
                    </FormField>
                </Grid>
            }
            <Grid item sm={12}>
                <FormField title={Translations.Report} margin={props.formFieldMargin}>
                    <IdTitleDropdown
                        selectedId={objectValue.reportId}
                        items={(props.eventReports ?? []).map(i => {
                            return { id: i.id, title: i.title };
                        })}
                        onChange={(reportId) => {
                            objectValue.reportId = reportId;
                            objectValue.name = getName(reportId, objectValue.chartId);
                            handleObjectValueChange(objectValue);
                        }}
                    />
                </FormField>
            </Grid>
            <Grid item sm={12}>
                <FormField title={Translations.ChartOrTableType} margin={props.formFieldMargin}>
                    <IdTitleDropdown
                        selectedId={objectValue.chartId}
                        items={(report?.children ?? []).map(i => {
                            return { id: i.id, title: i.title };
                        })}
                        onChange={(chartId) => {
                            objectValue.chartId = chartId;
                            objectValue.name = getName(objectValue.reportId, chartId);
                            handleObjectValueChange(objectValue);
                        }}
                    />
                </FormField>
            </Grid>
            {!!state.extraParameters &&
                <Box sx={{ marginTop: "1rem", marginBottom: "-1rem", marginRight: "-1rem", width: "calc(100% + 1rem)" }}>
                    <Box sx={{ width: "100%" }}>
                        <TaskDynamicForm
                            taskTypeCols={state.taskType?.formCols}
                            taskDataRowCols={state.extraParameters.cols}
                            onRefreshCols={(taskDataRowCols: ITaskDataRowCol[]) => {
                                const extraParameters = new TaskDataRow(state.extraParameters);
                                extraParameters.cols = taskDataRowCols;
                                objectValue.reportFilter = SaveReportDialog.getReportFilterFromTaskType(state.taskType, extraParameters);
                                handleObjectValueChange(objectValue);
                            }}
                        />
                    </Box>
                </Box>
            }
            <Grid item sm={12}>
                <FormField title={Translations.Unit} margin={props.formFieldMargin}>
                    <TreeCheckboxDropdown
                        required={true}
                        selectedIds={state.selectedUnitIds}
                        values={props.eventUnits || []}
                        onChange={(selectedUnitIds: string[]) => {
                            const newState = { ...state, selectedUnitIds };
                            objectValue.generalFilter = getGeneralFilter(newState);
                            handleObjectValueChange(objectValue);
                        }}
                    />
                </FormField>
            </Grid>
            <Grid item sm={12}>
                <FormField title={Translations.State} margin={props.formFieldMargin}>
                    <CheckboxDropdown
                        required={true}
                        selectedIds={state.selectedStateIds}
                        toggleAll={true}
                        toggleAllId={EventStateFilter.All.toString(10)}
                        values={props.eventStates || []}
                        onChange={(selectedStateIds: string[]) => {
                            const newState = { ...state, selectedStateIds };
                            objectValue.generalFilter = getGeneralFilter(newState);
                            handleObjectValueChange(objectValue);
                        }}
                    />
                </FormField>
            </Grid>
            <Grid item sm={12}>
                <FormField title={Translations.EventUrgencyClass} margin={props.formFieldMargin}>
                    <CheckboxDropdown
                        required={true}
                        selectedIds={state.selectedUrgencyClassIds}
                        toggleAll={true}
                        toggleAllId={Base.emptyGuid}
                        values={props.eventUrgencyClasses || []}
                        onChange={(selectedUrgencyClassIds: string[]) => {
                            const newState = { ...state, selectedUrgencyClassIds };
                            objectValue.generalFilter = getGeneralFilter(newState);
                            handleObjectValueChange(objectValue);
                        }}
                    />
                </FormField>
            </Grid>
            <Grid item sm={12}>
                <FormField title={Translations.EventType} margin={props.formFieldMargin}>
                    <TreeCheckboxDropdown
                        required={true}
                        toggleNotSetId={EventTypeNotSetId}
                        selectedIds={state.selectedEventTypeIds}
                        values={props.eventTypes || []}
                        onChange={(selectedEventTypeIds: string[]) => {
                            const newState = { ...state, selectedEventTypeIds };
                            objectValue.generalFilter = getGeneralFilter(newState);
                            handleObjectValueChange(objectValue);
                        }}
                    />
                </FormField>
            </Grid>
            <Grid item sm={12}>
                <FormField title={Translations.EventTypeB} margin={props.formFieldMargin}>
                    <TreeCheckboxDropdown
                        required={true}
                        toggleNotSetId={EventTypeNotSetId}
                        selectedIds={state.selectedEventTypeBIds}
                        values={props.eventTypeBs || []}
                        onChange={(selectedEventTypeBIds: string[]) => {
                            const newState = { ...state, selectedEventTypeBIds };
                            objectValue.generalFilter = getGeneralFilter(newState);
                            handleObjectValueChange(objectValue);
                        }}
                    />
                </FormField>
            </Grid>
            <Grid item sm={12}>
                <FormField title={Translations.EventTypeC} margin={props.formFieldMargin}>
                    <TreeCheckboxDropdown
                        required={true}
                        toggleNotSetId={EventTypeNotSetId}
                        selectedIds={state.selectedEventTypeCIds}
                        values={props.eventTypeCs || []}
                        onChange={(selectedEventTypeCIds: string[]) => {
                            const newState = { ...state, selectedEventTypeCIds };
                            objectValue.generalFilter = getGeneralFilter(newState);
                            handleObjectValueChange(objectValue);
                        }}
                    />
                </FormField>
            </Grid>
        </>
    );
};
